import { AutoComplete, Button } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { SearchOutlined } from '@ant-design/icons'

const DEFAULT_DATA = [
  { code: 'AEABU', name: 'Abu al Bukhoosh' },
  { code: 'AEAJM', name: 'Ajman' },
  { code: 'AEAMF', name: 'Mussafah' },
  { code: 'AEAMU', name: 'Abu Musa' },
  { code: 'AEAUH', name: 'Abu Dhabi' },
  { code: 'AEDAS', name: 'Das Island' },
  { code: 'AEDBP', name: 'Dibba' },
  { code: 'AEDUJ', name: 'The Palm Jumeirah' },
  { code: 'AEDXB', name: 'Dubai' },
  { code: 'AEEND', name: 'Esnnad' },
  { code: 'AEFAT', name: 'Fateh Terminal' },
  { code: 'AEFJR', name: 'Al Fujayrah' },
  { code: 'AEHTL', name: 'Hulaylah Terminal' },
  { code: 'AEJEA', name: 'Jebel Ali' },
  { code: 'AEJED', name: 'Jebel Dhanna' },
  { code: 'AEKHL', name: 'Mina Khalifa/Abu Dhabi' },
  { code: 'AEKLB', name: 'Kalba' },
  { code: 'AEKLF', name: 'Khor al Fakkan' },
]

const defaultRenderLabel = item => `${item.code} - ${item.name}`

export default function AutoCompleteSearch({
  data,
  disabled,
  onChange,
  onSelect,
  placeholder,
  renderLabel,
  value,
  valueKey,
}) {
  const [inputValue, setInputValue] = useState('')
  const [filterValue, setFilterValue] = useState('')
  const [open, setOpen] = useState(false)

  // Single source of truth for selected item
  const selectedItem = useMemo(() => {
    if (!value) return null

    return data.find(item => item[valueKey] === value) ?? null
  }, [data, value, valueKey])

  // Sync external value -> display value
  useEffect(() => {
    setInputValue(selectedItem ? renderLabel(selectedItem) : '')
  }, [selectedItem, renderLabel])

  const options = useMemo(() => {
    const query = filterValue.toLowerCase()

    return data
      .filter(item => {
        if (!query) return true

        return (
          item.code.toLowerCase().includes(query) ||
          item.name.toLowerCase().includes(query)
        )
      })
      .map(item => ({
        item,
        label: renderLabel(item),
        value: item[valueKey],
      }))
  }, [data, filterValue, renderLabel, valueKey])

  const handleSelect = (val, option) => {
    const item = option.item

    setInputValue(renderLabel(item))
    setFilterValue('')
    setOpen(false)

    onChange?.(val)
    onSelect?.(item)
  }

  const handleBlur = () => {
    setInputValue(selectedItem ? renderLabel(selectedItem) : '')
    setOpen(false)
  }

  return (
    <div style={{ display: 'flex', width: '100%' }}>
      <AutoComplete
        disabled={disabled}
        onBlur={handleBlur}
        onDropdownVisibleChange={setOpen}
        onSelect={handleSelect}
        open={open}
        options={options}
        placeholder={placeholder}
        style={{ width: '100%' }}
        value={inputValue}
        onSearch={val => {
          setInputValue(val)
          setFilterValue(val)
          setOpen(true)

          if (!val) {
            onChange?.('')
            onSelect?.(null)
          }
        }}
      />

      <Button
        disabled={disabled}
        icon={<SearchOutlined />}
        type="primary"
        onClick={() => {
          setFilterValue('')
          setOpen(true)
        }}
      />
    </div>
  )
}

AutoCompleteSearch.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  placeholder: PropTypes.string,
  renderLabel: PropTypes.func,
  value: PropTypes.string,
  valueKey: PropTypes.oneOf(['code', 'name']),
}

AutoCompleteSearch.defaultProps = {
  data: DEFAULT_DATA,
  disabled: false,
  onChange: undefined,
  onSelect: undefined,
  placeholder: 'Search',
  renderLabel: defaultRenderLabel,
  value: '',
  valueKey: 'code',
}
